import { useState, ChangeEvent } from "react";
import {
  Box,
  Flex,
  Input,
  SecondaryButton,
  Text,
  UnderlineButton,
} from "flicket-ui";
import { useSinglePageCheckoutContext } from "~features/checkout/common/hooks/singlePageCheckoutContext";
import { getError, loadingPromise, showToast } from "~lib/helpers";
import { trim } from "lodash";
import { useOrderContext } from "~features/checkout/common/hooks/OrderContext";
import { getPromoCodeLabel } from "~services/order/hooks/useCalculatePromoCodeDiscount";
import { useOrganization } from "~hooks/useOrganization";

/**
 * PromoCodeInput component for applying/removing promo codes
 * Used in StandardCheckout order summary
 */
export function PromoCodeInput() {
  const { applyCoupon, removeCoupon, mutate } = useSinglePageCheckoutContext();
  const { order } = useOrderContext();
  const { organization } = useOrganization();

  const [inputValue, setInputValue] = useState("");
  const [activityState, setActivityState] = useState<
    "idle" | "applying" | "removing"
  >("idle");

  const hasExistingPromoCode = !!order?.promoCode;

  const applyPromoCode = async () => {
    if (!inputValue) {
      showToast("Please enter a promo code", "error");
      return;
    }

    setActivityState("applying");

    const promoCode = trim(inputValue);

    try {
      const result = await loadingPromise(async () =>
        applyCoupon({ promoCode })
      );

      if (result) {
        showToast("Promo code applied successfully", "success");
        setInputValue("");
        await mutate(); // Refresh order data
      }
    } catch (error) {
      showToast(getError(error, "graphQL"), "error");
    } finally {
      setActivityState("idle");
    }
  };

  const removePromoCode = async () => {
    if (!order?.promoCode) return;

    setActivityState("removing");

    try {
      await loadingPromise(async () => removeCoupon());
      showToast("Promo code removed", "success");
      setInputValue("");
      await mutate(); // Refresh order data
    } catch (error) {
      showToast(getError(error, "graphQL"), "error");
    } finally {
      setActivityState("idle");
    }
  };

  return (
    <Box width={"100%"}>
      {hasExistingPromoCode ? (
        <Flex backgroundColor={"N100"} p={2} borderRadius={"md"}>
          <Box width={"100%"}>
            <Text variant="regular" fontWeight={"bold"}>
              {order.promoCode.code}
            </Text>
            <Text
              variant={"small"}
              opacity={activityState === "removing" ? 0.5 : 1}
            >
              {getPromoCodeLabel(order.promoCode, organization?.currency)}
            </Text>
          </Box>
          <UnderlineButton
            onClick={() => {
              void removePromoCode();
            }}
            color={"N800"}
            disabled={activityState !== "idle"}
          >
            Remove
          </UnderlineButton>
        </Flex>
      ) : (
        <Flex>
          <Input
            w="100%"
            value={inputValue}
            disabled={activityState !== "idle"}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setInputValue(e.target.value)
            }
            placeholder="Enter promo code"
            mr={1}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void applyPromoCode();
              }
            }}
          />
          <SecondaryButton
            type="button"
            whiteSpace="nowrap"
            alignSelf="flex-end"
            disabled={activityState !== "idle"}
            maxW={"25%"}
            onClick={(e) => {
              e.preventDefault();
              void applyPromoCode();
            }}
            isLoading={activityState === "applying"}
          >
            Apply
          </SecondaryButton>
        </Flex>
      )}
    </Box>
  );
}
